import { EARTH_RADIUS_M } from '@app/consts';

const WORLD_EXTENT_M = 2.0 * Math.PI * EARTH_RADIUS_M;
const EPS_LON = 1e-12;
const EPS_LAT = 1e-9;
const MAX_MERCATOR_LAT = 85.05112878;
const MIN_MERCATOR_LAT = -85.05112878;
const MAX_BRANCHING = 3;

// Represents a rectangular geographic area (bounding box)
export class BoundingBox {
    latMin: number;
    lonMin: number;
    latMax: number;
    lonMax: number;

    constructor(latMin: number, lonMin: number, latMax: number, lonMax: number) {
        if (latMin > latMax) {
            throw new Error('Invalid bounding box: latMin must be <= latMax');
        }
        if (lonMin > lonMax) {
            throw new Error('Invalid bounding box: lonMin must be <= lonMax');
        }

        if (lonMin < -180 || lonMax > 180) {
            throw new Error('Longitude must be in range [-180, 180]');
        }

        if (latMin < MIN_MERCATOR_LAT || latMax > MAX_MERCATOR_LAT) {
            console.warn(
                `Latitude out of range, clamping to [${MIN_MERCATOR_LAT}, ${MAX_MERCATOR_LAT}]`,
            );
            this.latMin = Math.max(MIN_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, latMin));
            this.latMax = Math.max(MIN_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, latMax));
        } else {
            this.latMin = latMin;
            this.latMax = latMax;
        }

        this.lonMin = lonMin;
        this.lonMax = lonMax;
    }

    toObject() {
        return {
            latMin: this.latMin,
            lonMin: this.lonMin,
            latMax: this.latMax,
            lonMax: this.lonMax,
        };
    }
}

// Configuration options for the map tiling handler
export interface MapTilingConfig {
    branching: number;
    minLevel: number;
    maxLevel: number;
    epsilonRatio?: number;
}

// Handles map tiling logic, including generating tile bounding boxes
export class MapTilingHandler {
    config: MapTilingConfig;

    constructor(config: MapTilingConfig) {
        this.config = config;
        this.validateConfig();
    }

    private clampLatitude(lat: number): number {
        return Math.max(MIN_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
    }

    validateConfig() {
        if (this.config.branching < 2) {
            throw new Error('Invalid branching factor');
        }
        if (this.config.branching > MAX_BRANCHING) {
            console.warn(`Branching too high, clamping to ${MAX_BRANCHING}`);
            this.config.branching = MAX_BRANCHING;
        }
        if (this.config.minLevel < 0) {
            throw new Error('minLevel must be >= 0');
        }
        if (this.config.maxLevel < this.config.minLevel) {
            throw new Error('maxLevel must be >= minLevel');
        }
    }

    clampLevel(level: number) {
        return Math.max(this.config.minLevel, Math.min(this.config.maxLevel, level));
    }

    numberOfTiles(level: number) {
        return Math.pow(this.config.branching, this.clampLevel(level));
    }

    tileSizeMetersAt(level: number) {
        return WORLD_EXTENT_M / this.numberOfTiles(level);
    }

    mercatorX(lon: number) {
        return (EARTH_RADIUS_M * lon * Math.PI) / 180;
    }

    mercatorY(lat: number) {
        lat = this.clampLatitude(lat);
        return EARTH_RADIUS_M * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
    }

    tileIndicesForLatLon(lat: number, lon: number, level: number): [number, number] {
        const n = this.numberOfTiles(level);
        const x = Math.floor(((lon + 180.0) / 360.0) * n);
        lat = this.clampLatitude(lat);
        const y = Math.floor(
            ((1.0 -
                Math.log(Math.tan((lat * Math.PI) / 180) + 1.0 / Math.cos((lat * Math.PI) / 180)) /
                    Math.PI) /
                2.0) *
                n,
        );
        return [x, y];
    }

    tileBoundingBox(zoom: number, x: number, y: number): BoundingBox {
        const n = this.numberOfTiles(zoom);
        const lonMin = (x / n) * 360.0 - 180.0;
        const lonMax = ((x + 1) / n) * 360.0 - 180.0;
        const latMax = (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
        const latMin = (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n)));
        return new BoundingBox(latMin, lonMin, latMax, lonMax);
    }

    tileTopLeft(level: number, x: number, y: number) {
        const n = this.numberOfTiles(level);
        const lon = (x / n) * 360.0 - 180.0;
        const lat = (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
        return { lat, lon };
    }

    tileBottomRight(level: number, x: number, y: number) {
        const n = this.numberOfTiles(level);
        const lon = ((x + 1) / n) * 360.0 - 180.0;
        const lat = (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n)));
        return { lat, lon };
    }

    tileSpanIndicesForBox(bbox: BoundingBox, level: number) {
        const [xW, yN] = this.tileIndicesForLatLon(bbox.latMax, bbox.lonMin, level);
        const [xE, yS] = this.tileIndicesForLatLon(bbox.latMin, bbox.lonMax, level);
        return { xW, xE, yN, yS };
    }

    getTileBoundingBoxesInsideCover(bbox: BoundingBox, baseZoom: number, depth = 1): BoundingBox[] {
        const targetZoom = this.clampLevel(baseZoom);
        const span = this.tileSpanIndicesForBox(bbox, targetZoom);
        const n = this.numberOfTiles(targetZoom);
        const wrapX = (x: number) => ((x % n) + n) % n;
        const clampY = (y: number) => Math.max(0, Math.min(n - 1, y));

        const boxes: BoundingBox[] = [];
        for (let y = span.yN; y <= span.yS; y++) {
            for (let x = span.xW; x <= span.xE; x++) {
                const bboxCandidate = this.tileBoundingBox(targetZoom, wrapX(x), clampY(y));
                if (this.intersects(bbox, bboxCandidate)) {
                    boxes.push(bboxCandidate);
                }
            }
        }
        return boxes;
    }

    intersects(a: BoundingBox, b: BoundingBox) {
        return (
            a.latMin < b.latMax && a.latMax > b.latMin && a.lonMin < b.lonMax && a.lonMax > b.lonMin
        );
    }

    neighborsRingAroundChildren(bbox: BoundingBox, zoom: number, depth = 1): BoundingBox[] {
        const target = this.clampLevel(zoom + Math.max(1, depth));
        const span = this.tileSpanIndicesForBox(bbox, target);
        const n = this.numberOfTiles(target);
        const wrapX = (x: number) => ((x % n) + n) % n;
        const clampY = (y: number) => Math.max(0, Math.min(n - 1, y));

        const seen = new Set<string>();
        const out: BoundingBox[] = [];

        const add = (x: number, y: number) => {
            const key = `${wrapX(x)}:${clampY(y)}`;
            if (seen.has(key)) return;
            seen.add(key);
            out.push(this.tileBoundingBox(target, wrapX(x), clampY(y)));
        };

        for (let x = span.xW; x <= span.xE; x++) add(x, span.yN - 1);
        for (let x = span.xW; x <= span.xE; x++) add(x, span.yS + 1);
        for (let y = span.yN; y <= span.yS; y++) add(span.xW - 1, y);
        for (let y = span.yN; y <= span.yS; y++) add(span.xE + 1, y);

        [
            [-1, -1],
            [1, -1],
            [-1, 1],
            [1, 1],
        ].forEach(([dx, dy]) => {
            add(span.xW + dx, span.yN + dy);
            add(span.xE + dx, span.yS + dy);
        });

        return out;
    }
}

interface TileCoordinates {
    x: number;
    y: number;
    zoom: number;
}

// Represents a visual label for a tile that can be added as an OverlayView on map
export class TileLabel {
    id: number;
    bounds: BoundingBox;
    div: HTMLDivElement;
    overlay?: google.maps.OverlayView;

    constructor(id: number, bounds: BoundingBox, tileIndices: TileCoordinates) {
        this.id = id;
        this.bounds = bounds;
        this.div = document.createElement('div');
        this.div.style.position = 'absolute';
        this.div.style.backgroundColor = 'rgba(255,255,255,0.7)';
        this.div.style.padding = '2px 4px';
        this.div.style.fontSize = '12px';
        this.div.style.pointerEvents = 'none';
        this.div.style.border = '1px solid #000';
        this.div.style.zIndex = '999';

        const labelLines = [
            `Tile #${id}`,
            `lat: [${bounds.latMin.toFixed(4)}, ${bounds.latMax.toFixed(4)}]`,
            `lon: [${bounds.lonMin.toFixed(4)}, ${bounds.lonMax.toFixed(4)}]`,
            `x:${tileIndices.x} y:${tileIndices.y} z:${tileIndices.zoom}`,
        ];
        this.div.innerText = labelLines.join('\n');
        this.div.className = `tile-label-${id}`;
    }

    addToMap(map: google.maps.Map) {
        if (!window.google) return;

        class ClientOverlay extends google.maps.OverlayView {
            onAdd() {
                this.getPanes()?.overlayLayer.appendChild(div);
            }
            draw() {
                const projection = this.getProjection();
                if (!projection) return;
                const ne = projection.fromLatLngToDivPixel(
                    new google.maps.LatLng(bounds.latMax, bounds.lonMax),
                );
                const sw = projection.fromLatLngToDivPixel(
                    new google.maps.LatLng(bounds.latMin, bounds.lonMin),
                );
                div.style.left = `${sw?.x}px`;
                div.style.top = `${ne?.y}px`;
            }
            onRemove() {
                div.parentNode?.removeChild(div);
            }
        }

        const div = this.div;
        const bounds = this.bounds;
        this.overlay = new ClientOverlay();
        this.overlay.setMap(map);
    }

    remove() {
        this.overlay?.setMap(null);
    }
}

export const areTilesEqual = (a: BoundingBox[], b: BoundingBox[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (
            a[i].latMin !== b[i].latMin ||
            a[i].latMax !== b[i].latMax ||
            a[i].lonMin !== b[i].lonMin ||
            a[i].lonMax !== b[i].lonMax
        )
            return false;
    }
    return true;
};

export const areBoundsInside = (
    inner: google.maps.LatLngBoundsLiteral,
    outer: google.maps.LatLngBoundsLiteral,
): boolean => {
    return (
        inner.north <= outer.north &&
        inner.south >= outer.south &&
        inner.east <= outer.east &&
        inner.west >= outer.west
    );
};

export const areTilesInside = (prevTiles: BoundingBox[], newTiles: BoundingBox[]) => {
    return newTiles.every(curr =>
        prevTiles.some(
            prev =>
                curr.latMin >= prev.latMin &&
                curr.latMax <= prev.latMax &&
                curr.lonMin >= prev.lonMin &&
                curr.lonMax <= prev.lonMax,
        ),
    );
};

export const isWorldBounds = (bounds: google.maps.LatLngBoundsLiteral) => {
    return (
        bounds.north === 90 && bounds.south === -90 && bounds.east === 180 && bounds.west === -180
    );
};

export const createRectangle = (
    bounds: google.maps.LatLngBoundsLiteral,
    options?: google.maps.RectangleOptions,
) => {
    return new google.maps.Rectangle({
        bounds,
        ...options,
    });
};
